import { useState, useRef } from "react";
import ImageUploader from "../../../components/base/ImageUploader";
import RichTextEditor from "../../../components/base/RichTextEditor";

export interface SectionItem {
  title: string;
  content: string;
  image: string;
  imagePosition: "left" | "right";
  accent?: string;
}

interface Props {
  lang: "vi" | "kr";
  sections: SectionItem[];
  onChange: (sections: SectionItem[]) => void;
}

const emptySection = (): SectionItem => ({
  title: "",
  content: "",
  image: "",
  imagePosition: "left",
  accent: "",
});

const SectionsRepeater: React.FC<Props> = ({ lang, sections, onChange }) => {
  const t = (vi: string, kr: string) => (lang === "vi" ? vi : kr);
  const [expandedIdx, setExpandedIdx] = useState<number | null>(sections.length > 0 ? 0 : null);
  const dragIdx = useRef<number | null>(null);
  const dragOverIdx = useRef<number | null>(null);

  const addSection = () => {
    const next = [...sections, emptySection()];
    onChange(next);
    setExpandedIdx(next.length - 1);
  };

  const removeSection = (idx: number) => {
    const next = sections.filter((_, i) => i !== idx);
    onChange(next);
    if (expandedIdx === idx) setExpandedIdx(null);
    else if (expandedIdx !== null && expandedIdx > idx) setExpandedIdx(expandedIdx - 1);
  };

  const updateSection = (idx: number, field: keyof SectionItem, val: string) => {
    const next = sections.map((s, i) => (i === idx ? { ...s, [field]: val } : s));
    onChange(next);
  };

  const moveUp = (idx: number) => {
    if (idx === 0) return;
    const next = [...sections];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    onChange(next);
    setExpandedIdx(idx - 1);
  };

  const moveDown = (idx: number) => {
    if (idx === sections.length - 1) return;
    const next = [...sections];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    onChange(next);
    setExpandedIdx(idx + 1);
  };

  const handleDragStart = (idx: number) => { dragIdx.current = idx; };
  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    dragOverIdx.current = idx;
  };
  const handleDrop = () => {
    if (dragIdx.current === null || dragOverIdx.current === null) return;
    if (dragIdx.current === dragOverIdx.current) return;
    const next = [...sections];
    const [moved] = next.splice(dragIdx.current, 1);
    next.splice(dragOverIdx.current, 0, moved);
    onChange(next);
    setExpandedIdx(dragOverIdx.current);
    dragIdx.current = null;
    dragOverIdx.current = null;
  };

  const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:border-[#004A99] outline-none bg-white";
  const labelCls = "block text-xs font-semibold text-gray-600 mb-1";

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-bold text-gray-800">
            {t("Câu chuyện sản phẩm (Zigzag)", "제품 스토리 (지그재그)")}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {t("Kéo thả để sắp xếp thứ tự. Thẻ lẻ: ảnh trái, thẻ chẵn: ảnh phải.", "드래그하여 순서 변경. 홀수: 이미지 왼쪽, 짝수: 이미지 오른쪽.")}
          </p>
        </div>
        <button
          type="button"
          onClick={addSection}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#004A99] text-white text-xs rounded-lg cursor-pointer hover:bg-[#003d7d] transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line" />
          {t("Thêm thẻ", "카드 추가")}
        </button>
      </div>

      {/* Empty state */}
      {sections.length === 0 && (
        <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center">
          <i className="ri-layout-column-line text-3xl text-gray-300 block mb-2" />
          <p className="text-sm text-gray-400">{t("Chưa có thẻ nào", "카드 없음")}</p>
          <p className="text-xs text-gray-400 mt-1">
            {t("Bấm \"Thêm thẻ\" để tạo nội dung zigzag", "\"카드 추가\" 버튼을 눌러 지그재그 콘텐츠를 만드세요")}
          </p>
        </div>
      )}

      {/* Section list */}
      {sections.map((section, idx) => (
        <div
          key={idx}
          draggable
          onDragStart={() => handleDragStart(idx)}
          onDragOver={(e) => handleDragOver(e, idx)}
          onDrop={handleDrop}
          className="border border-gray-200 rounded-xl overflow-hidden bg-white"
        >
          {/* Section header */}
          <div
            className="flex items-center gap-3 px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
            onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
          >
            {/* Drag handle */}
            <div className="w-5 h-5 flex items-center justify-center text-gray-400 cursor-grab flex-shrink-0">
              <i className="ri-drag-move-2-line text-sm" />
            </div>

            {/* Index badge */}
            <span
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                idx % 2 === 0 ? "bg-[#004A99] text-white" : "bg-[#00AEEF] text-white"
              }`}
            >
              {idx + 1}
            </span>

            {/* Title */}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-gray-700 truncate">
                {section.title || t(`Thẻ ${idx + 1} — chưa có tiêu đề`, `카드 ${idx + 1} — 제목 없음`)}
              </p>
              <p className="text-[10px] text-gray-400 mt-0.5">
                {idx % 2 === 0
                  ? t("Ảnh bên trái · Chữ bên phải", "이미지 왼쪽 · 텍스트 오른쪽")
                  : t("Chữ bên trái · Ảnh bên phải", "텍스트 왼쪽 · 이미지 오른쪽")}
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={() => moveUp(idx)}
                disabled={idx === 0}
                className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-[#004A99] disabled:opacity-30 cursor-pointer rounded"
              >
                <i className="ri-arrow-up-s-line text-sm" />
              </button>
              <button
                type="button"
                onClick={() => moveDown(idx)}
                disabled={idx === sections.length - 1}
                className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-[#004A99] disabled:opacity-30 cursor-pointer rounded"
              >
                <i className="ri-arrow-down-s-line text-sm" />
              </button>
              <button
                type="button"
                onClick={() => removeSection(idx)}
                className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-red-500 cursor-pointer rounded"
              >
                <i className="ri-delete-bin-line text-sm" />
              </button>
              <div className="w-5 h-5 flex items-center justify-center text-gray-400">
                {expandedIdx === idx
                  ? <i className="ri-arrow-up-s-line text-sm" />
                  : <i className="ri-arrow-down-s-line text-sm" />
                }
              </div>
            </div>
          </div>

          {/* Section body */}
          {expandedIdx === idx && (
            <div className="p-4 space-y-4 border-t border-gray-100">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{t("Tiêu đề thẻ *", "카드 제목 *")}</label>
                  <input
                    className={inputCls}
                    value={section.title}
                    onChange={(e) => updateSection(idx, "title", e.target.value)}
                    placeholder={t("VD: Công nghệ tiên tiến", "예: 첨단 기술")}
                  />
                </div>
                <div>
                  <label className={labelCls}>{t("Nhãn accent (tùy chọn)", "액센트 레이블 (선택)")}</label>
                  <input
                    className={inputCls}
                    value={section.accent ?? ""}
                    onChange={(e) => updateSection(idx, "accent", e.target.value)}
                    placeholder={t("VD: CÔNG NGHỆ MỚI", "예: 신기술")}
                  />
                </div>
              </div>

              <div>
                <label className={labelCls}>{t("Vị trí ảnh", "이미지 위치")}</label>
                <div className="flex gap-2">
                  {(["left", "right"] as const).map((pos) => (
                    <button
                      key={pos}
                      type="button"
                      onClick={() => updateSection(idx, "imagePosition", pos)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold border transition-colors cursor-pointer whitespace-nowrap ${
                        section.imagePosition === pos
                          ? "bg-[#004A99] text-white border-[#004A99]"
                          : "bg-white text-gray-600 border-gray-200 hover:border-[#004A99]/40"
                      }`}
                    >
                      <i className={pos === "left" ? "ri-layout-left-2-line" : "ri-layout-right-2-line"} />
                      {pos === "left" ? t("Ảnh bên trái", "이미지 왼쪽") : t("Ảnh bên phải", "이미지 오른쪽")}
                    </button>
                  ))}
                </div>
              </div>

              <RichTextEditor
                value={section.content}
                onChange={(html) => updateSection(idx, "content", html)}
                placeholder={t("Nhập nội dung mô tả cho thẻ này...", "이 카드의 설명 내용을 입력하세요...")}
                minHeight={160}
                folder="products"
                label={t("Nội dung mô tả", "설명 내용")}
              />

              <div>
                <label className={labelCls}>{t("Hình ảnh thẻ", "카드 이미지")}</label>
                <ImageUploader
                  images={section.image ? [section.image] : []}
                  onChange={(urls) => updateSection(idx, "image", urls[0] ?? "")}
                  maxImages={1}
                  folder="products"
                  label={t("Ảnh minh họa cho thẻ này", "이 카드의 이미지")}
                />
              </div>

              {/* Preview */}
              {(section.title || section.image) && (
                <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                    {t("Xem trước bố cục", "레이아웃 미리보기")}
                  </p>
                  <div className={`flex gap-3 items-center ${section.imagePosition === "right" ? "flex-row-reverse" : ""}`}>
                    <div className="w-20 h-14 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
                      {section.image ? (
                        <img src={section.image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <i className="ri-image-line text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-gray-700 truncate">{section.title || "..."}</p>
                      {section.accent && (
                        <span className="text-[9px] bg-[#004A99] text-white px-1.5 py-0.5 rounded-full mt-1 inline-block">
                          {section.accent}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {/* Add button at bottom */}
      {sections.length > 0 && (
        <button
          type="button"
          onClick={addSection}
          className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-500 hover:border-[#004A99] hover:text-[#004A99] transition-colors cursor-pointer"
        >
          <i className="ri-add-line" />
          {t("Thêm thẻ mới", "새 카드 추가")}
        </button>
      )}
    </div>
  );
};

export default SectionsRepeater;
